import React from "react";
import { Link } from "react-router-dom";
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaArrowRight } from "react-icons/fa";
import Services3 from "../images/contact.jpg";
import WhatsApp     from "../inc/Whatsapp";

/* ── Constants ─────────────────────────────────────── */
const CONTACT_INFO = [
  {
    icon: <FaMapMarkerAlt />,
    label: "Our Office",
    value: "MRSL - 5, Bengal Ambuja HIG Complex, City Center, Durgapur, Paschim Bardhaman, WB - 713216, India",
    href: "https://maps.google.com/?q=23.5451376,87.2879256",
    linkLabel: "Get Directions →",
  },
  {
    icon: <FaPhone />,
    label: "Call Us",
    value: "+91 7318731222",
    href: "https://wa.me/917318731222",
    linkLabel: "Chat on WhatsApp →",
    target: "_blank",
  },
  {
    icon: <FaEnvelope />,
    label: "Email Us",
    value: "info@skywisetechnologies.in",
    href: "https://mail.google.com/mail/?view=cm&fs=1&to=info@skywisetechnologies.in",
    linkLabel: "Send Email →",
    target: "_blank",
  },
];

/* ── Component ─────────────────────────────────────── */
function Contact() {
  return (
    <>
      {/* ── Scoped Styles ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

        :root {
          --accent:       #007bff;
          --accent-dark:  #0056cc;
          --accent-glow:  rgba(0,123,255,0.18);
          --bg-page:      #f0f4f8;
          --bg-card:      #ffffff;
          --bg-input:     #f7f9fc;
          --text-dark:    #111827;
          --text-body:    #4b5563;
          --text-muted:   #9ca3af;
          --border:       #e5e7eb;
          --error:        #ef4444;
          --success:      #16a34a;
          --font-display: 'Syne', sans-serif;
          --font-body:    'DM Sans', sans-serif;
          --radius:       14px;
          --shadow-card:  0 4px 24px rgba(0,0,0,0.08);
          --shadow-hover: 0 8px 32px rgba(0,0,0,0.13);
        }

        .ct-page {
          background: var(--bg-page);
          font-family: var(--font-body);
          min-height: 100vh;
        }

        /* ── Hero Banner ── */
        .ct-hero {
          position: relative;
          min-height: 300px;
          background-size: cover;
          background-position: center;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .ct-hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(0,30,80,0.82) 0%, rgba(0,80,180,0.65) 100%);
        }
        .ct-hero-content {
          position: relative;
          z-index: 1;
          text-align: center;
          padding: 3.5rem 1rem;
        }
        .ct-hero-eyebrow {
          display: inline-block;
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.25);
          color: rgba(255,255,255,0.9);
          font-size: 0.78rem;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          padding: 0.3rem 1rem;
          border-radius: 50px;
          margin-bottom: 1rem;
        }
        .ct-hero-title {
          font-family: var(--font-display);
          font-size: clamp(2rem, 5vw, 3rem);
          font-weight: 800;
          color: #fff;
          margin: 0 0 0.5rem;
          line-height: 1.15;
        }
        .ct-hero-sub {
          font-size: 1rem;
          color: rgba(255,255,255,0.75);
          margin: 0;
          font-weight: 400;
        }

        /* ── Main container ── */
        .ct-container {
          max-width: 1180px;
          margin: 0 auto;
          padding: 0 1.25rem 4rem;
        }

        /* ── Floating card (overlaps hero) ── */
        .ct-card-wrap {
          margin-top: -56px;
          position: relative;
          z-index: 2;
        }

        /* ── Breadcrumb ── */
        .ct-breadcrumb {
          background: var(--bg-card);
          border-radius: var(--radius) var(--radius) 0 0;
          padding: 0.85rem 1.75rem;
          border-bottom: 1px solid var(--border);
          font-size: 0.82rem;
          color: var(--text-muted);
          display: flex;
          align-items: center;
          gap: 0.4rem;
        }
        .ct-breadcrumb a {
          color: var(--accent);
          text-decoration: none;
          font-weight: 500;
        }
        .ct-breadcrumb a:hover { text-decoration: underline; }

        /* ── Info + Form grid ── */
        .ct-body {
          background: var(--bg-card);
          border-radius: 0 0 var(--radius) var(--radius);
          box-shadow: var(--shadow-card);
          padding: 2.5rem 1.75rem;
        }

        .ct-section-header { margin-bottom: 2rem; }
        .ct-section-label {
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--accent);
          margin-bottom: 0.4rem;
        }
        .ct-section-title {
          font-family: var(--font-display);
          font-size: clamp(1.4rem, 3vw, 1.9rem);
          font-weight: 700;
          color: var(--text-dark);
          margin: 0 0 0.4rem;
        }
        .ct-underline {
          width: 48px; height: 3px;
          background: var(--accent);
          border-radius: 2px;
          margin-bottom: 0.9rem;
        }
        .ct-section-desc {
          font-size: 0.95rem;
          color: var(--text-body);
          line-height: 1.7;
          max-width: 480px;
        }

        /* ── Two-column layout ── */
        .ct-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 2.5rem;
          align-items: flex-start;
        }
        .ct-left  { flex: 1 1 100%; }

        /* ── Info cards ── */
        .ct-info-cards {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          margin-bottom: 2rem;
        }
        .ct-info-card {
          flex: 1 1 240px;
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          padding: 1.1rem 1.25rem;
          border: 1px solid var(--border);
          border-radius: 10px;
          background: var(--bg-input);
          transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
          text-decoration: none;
        }
        .ct-info-card:hover {
          border-color: var(--accent);
          box-shadow: 0 4px 16px var(--accent-glow);
          transform: translateY(-2px);
        }
        .ct-info-icon {
          width: 40px; height: 40px;
          border-radius: 8px;
          background: var(--accent-glow);
          color: var(--accent);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
          flex-shrink: 0;
        }
        .ct-info-label {
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--text-muted);
          margin-bottom: 0.2rem;
        }
        .ct-info-value {
          font-size: 0.88rem;
          color: var(--text-dark);
          font-weight: 500;
          line-height: 1.5;
          margin-bottom: 0.25rem;
        }
        .ct-info-link {
          font-size: 0.78rem;
          color: var(--accent);
          font-weight: 600;
        }

        /* ── Map ── */
        .ct-map {
          width: 100%;
          height: 400px;
          border-radius: 10px;
          border: 1px solid var(--border);
          overflow: hidden;
        }
        .ct-map iframe {
          width: 100%; height: 100%;
          border: none; display: block;
        }

        /* ── Responsive ── */
        @media (max-width: 640px) {
          .ct-card-wrap  { margin-top: -32px; }
          .ct-body       { padding: 1.5rem 1rem; }
          .ct-map        { height: 260px; }
          .ct-info-card  { flex: 1 1 100%; }
        }
        @media (max-width: 400px) {
          .ct-hero-title { font-size: 1.7rem; }
          .ct-breadcrumb { padding: 0.7rem 1rem; }
        }
      `}</style>

      <div className="ct-page">
        {/* ── Hero Banner ── */}
        <div
          className="ct-hero"
          style={{ backgroundImage: `url(${Services3})` }}
          role="banner"
        >
          <div className="ct-hero-overlay" />
          <div className="ct-hero-content">
            <span className="ct-hero-eyebrow">Get In Touch</span>
            <h1 className="ct-hero-title">Contact Us</h1>
            <p className="ct-hero-sub">
              We'd love to hear from you — let's build something great together.
            </p>
          </div>
        </div>

        {/* ── Main content ── */}
        <div className="ct-container">
          <div className="ct-card-wrap">
            {/* Breadcrumb */}
            <nav className="ct-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <FaArrowRight style={{ fontSize: "0.6rem", opacity: 0.5 }} />
              <span>Contact</span>
            </nav>

            {/* Body */}
            <div className="ct-body">
              {/* Section header */}
              <div className="ct-section-header">
                <div className="ct-section-label">Contact</div>
                <h2 className="ct-section-title">Let's Connect</h2>
                <div className="ct-underline" />
                <p className="ct-section-desc">
                  At <strong>Skywise Technologies</strong>, we're passionate about
                  helping businesses succeed through technology. Whether you need
                  tailored software, IT consulting, or innovative logistics solutions
                  — our team is here to help.
                </p>
              </div>

              {/* Two-column grid */}
              <div className="ct-grid">
                {/* ── Info + Map ── */}
                <div className="ct-left">
                  <div className="ct-info-cards">
                    {CONTACT_INFO.map((c) => (
                      <a
                        key={c.label}
                        href={c.href}
                        className="ct-info-card"
                        target={c.target ?? (c.label === "Our Office" ? "_blank" : undefined)}
                        rel={c.target || c.label === "Our Office" ? "noopener noreferrer" : undefined}
                      >
                        <div className="ct-info-icon">{c.icon}</div>
                        <div>
                          <div className="ct-info-label">{c.label}</div>
                          <div className="ct-info-value">{c.value}</div>
                          <div className="ct-info-link">{c.linkLabel}</div>
                        </div>
                      </a>
                    ))}
                  </div>

                  {/* Map */}
                  <div className="ct-map">
                    <iframe
                      title="Skywise Technologies Location"
                      src="https://www.google.com/maps?q=23.5451376,87.2879256&z=15&output=embed"
                      allowFullScreen
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <WhatsApp />
      </div>
    </>
  );
}

export default Contact;